// @ts-strict-ignore
import {VNode} from 'snabbdom';
import {AttrsSchema, Component} from 'panel';
import ResizeObserver from 'resize-observer-polyfill';

import {IronContextRegistry} from 'common/report/context-registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {DummyStorageManager} from 'common/contexts/storage-manager/dummy';
import {LocalStorageKey} from 'common/report/constants';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {emitTrackEvent, MOUSE_BUTTONS} from 'common/util/dom';
import {generateShortcutLabel, keyEventSwitcher} from 'common/util/keyboard';
import Layout from 'common/stylesheets/mixins/layout.json';
import {ReportInfo} from 'common/report/contexts/report-info';
import {isResizablePanelCollapsed} from 'common/components/resizable-panel/utils';
import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';

import {Placement} from '../tooltip/types';
import {
  CollapsePanelSource,
  Direction,
  ExpandPanelSource,
  OtherTogglePanelSource,
  PanelVisibility,
  PersistedResizablePanelState,
  ResetPanelSource,
  ResizablePanelAttrs,
  ResizablePanelJsxProps,
  ResizablePanelState,
  ResizeToCollapseMode,
  Theme,
  ToggleAlignmentSource,
  TogglePanelSource,
  TrackingEvent,
} from './types';
import {
  ALIGN_DIRECTION_LABEL,
  DEFAULT_PERSISTED_STATE,
  HORIZONTAL_DIRECTIONS,
  KEYDOWN_EVENTS_CHARACTER,
  NEGATIVE_DIRECTIONS,
  OPPOSITE_DIRECTION,
  PANEL_COLLAPSED_WIDTH,
  PANEL_DEFAULT_WIDTH,
  PANEL_MAX_AUTO_WIDTH,
  PANEL_MAX_WIDTH,
  PANEL_MIN_WIDTH,
  PANEL_RESIZE_SNAP_WINDOW,
  VERTICAL_DIRECTIONS,
} from './constants';
import {template} from './template';
import css from './index.inline.styl';

const COMPONENT_NAME = `mp-resizable-panel`;
const {PAGE_MIN_WIDTH} = Layout;

export class ResizablePanel extends Component<ResizablePanelState, ResizablePanelAttrs, any, any, IronContextRegistry> {
  private declare resizeHandler: (ev: MouseEvent) => void;
  private declare userLocalStorageHelper: UserLocalStorageHelper<PersistedResizablePanelState>;
  private declare containerResizeObserver: ResizeObserver;
  private declare _containerElement: HTMLElement;
  protected _headerContent: VNode = null;
  protected _footerContent?: VNode = null;

  private hasDispatchedResize = false;

  static get attrsSchema(): AttrsSchema<ResizablePanelAttrs> {
    return {
      'disable-collapsing': `boolean`,
      loading: {type: `boolean`},
      'min-adjacent-size': {
        type: `number`,
        default: PAGE_MIN_WIDTH - PANEL_MIN_WIDTH,
        description: `minimum size for the adjacent content on the page. used to block resizing and collapsing the panel when necessary.`,
      },
      'min-size': {
        type: `number`,
        default: PANEL_MIN_WIDTH,
        description: `minimum size for the panel content`,
      },
      'max-size': {
        type: `number`,
        default: PANEL_MAX_WIDTH,
        description: `maximum size for the panel content`,
      },
      'max-auto-size': {
        type: `number`,
        default: PANEL_MAX_AUTO_WIDTH,
        description: `suggested size presented to the user by default`,
      },
      'default-size': {
        type: `number`,
        default: PANEL_DEFAULT_WIDTH,
        description: `default size of the panel content`,
      },
      'storage-key': {type: `string`, default: `default`},
      'show-collapse-button': `boolean`,
      'resize-to-collapse-mode': {
        type: `string`,
        default: ResizeToCollapseMode.Default,
        enum: Object.values(ResizeToCollapseMode),
      },
      'show-alignment-toggle': `boolean`,
      'panel-name': `string`,
      'default-alignment': {type: `string`, default: Direction.Left},
      'collapse-key': `string`,
      'collapse-key-modifier': {type: `string`, tsType: `KeyModifiers`},
      'default-ratio': `number`,
      'fixed-size': `boolean`,
      'disable-responsive-resize': {
        type: `boolean`,
        description: `prevent the panel from responsively resizing when the container's dimensions changes`,
      },
      theme: {
        type: `string`,
        enum: Object.values(Theme),
        default: Theme.Default,
      },
      visibility: {
        type: `string`,
        enum: Object.values(PanelVisibility),
        default: PanelVisibility.Auto,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`localStorageManager` as const, `reportInfo` as const, `i18n` as const, `currentProject` as const],
      defaultContexts: {
        localStorageManager: new DummyStorageManager(),
        reportInfo: new ReportInfo(),
      },
      defaultState: {
        hasOpenDropdown: false,
        isHoveringAlignmentToggle: false,
        canExpand: true,
        hasResized: false,
        isResizing: false,
        containerWidth: 0,
        containerHeight: 0,
        ...DEFAULT_PERSISTED_STATE,
      },
      hooks: {
        postUpdate: (_state: ResizablePanelState) => {
          this.applyStyles();
        },
      },
      helpers: {
        getAlignment: () => {
          const persistedState = this.userLocalStorageHelper?.getItem({
            key: this.attr(`storage-key`),
            userId: userCookieUtils.getUserIdentityFromCookie()?.id,
          });

          return this.state.alignment
            ? this.state.alignment
            : persistedState?.alignment
              ? persistedState.alignment
              : this.attr(`default-alignment`);
        },
        getResizeDirection: () => {
          // Resize in the opposite direction to the alignment.
          return OPPOSITE_DIRECTION[this.helpers.getAlignment()];
        },

        getCollapseButtonIconDirection: () => {
          switch (this.helpers.getResizeDirection()) {
            case Direction.Top:
              return this.isCollapsed() ? `push-up` : `push-down`;
            case Direction.Bottom:
              return this.isCollapsed() ? `push-down` : `push-up`;
            case Direction.Right:
              return this.isCollapsed() ? `push-right` : `push-left`;
            case Direction.Left:
              return this.isCollapsed() ? `push-left` : `push-right`;
            default:
              return null;
          }
        },

        getAlignmentToggleIcon: () => {
          switch (this.helpers.getAlignment()) {
            case Direction.Right:
              return this.state.isHoveringAlignmentToggle ? `right-panel-hover` : `right-panel`;
            case Direction.Left:
              return this.state.isHoveringAlignmentToggle ? `left-panel-hover` : `left-panel`;
            default:
              return null;
          }
        },

        getAlignmentToggleTooltipText: () => {
          const newDirection: string = OPPOSITE_DIRECTION[this.helpers.getAlignment()];
          // TODO (Alix): replace with finalized copy
          return ALIGN_DIRECTION_LABEL[newDirection];
        },

        getSideButtonTooltipDirection: () => {
          switch (this.helpers.getResizeDirection()) {
            case Direction.Top:
              return this.isCollapsed() ? Placement.Top : Placement.RightCenter;
            case Direction.Bottom:
              return this.isCollapsed() ? Placement.Bottom : Placement.RightCenter;
            case Direction.Right:
              return this.isCollapsed() ? Placement.RightEnd : Placement.Top;
            case Direction.Left:
              return this.isCollapsed() ? Placement.LeftEnd : Placement.Top;
            default:
              return null;
          }
        },
      },
    };
  }

  get currentProject(): CurrentProject | undefined {
    return this.getContext(`currentProject`).get().value;
  }

  getContainerElement() {
    return this._containerElement || this.parentElement;
  }

  connectedCallback() {
    this.userLocalStorageHelper = new UserLocalStorageHelper({
      context: this.getContext(`localStorageManager`),
      namespace: LocalStorageKey.Panel,
    });
    this.update(this.getPersistedState());

    super.connectedCallback();

    const setContainerSizes = (rect: DOMRect) => {
      const {width: containerWidth, height: containerHeight} = rect;
      this.update({containerWidth, containerHeight});
      this.handleContainerResize();
    };

    if (!this.attr(`disable-responsive-resize`)) {
      this.containerResizeObserver = new ResizeObserver((entries) => {
        setContainerSizes(entries[0].contentRect);
      });
      this.containerResizeObserver.observe(this.getContainerElement());
    }

    // Need to call this synchronously so that this element's size is set immediately, since other components might
    // rely on this element having the proper size (e.g. <mp-data-table> needs this to determine number of rows to
    // render)
    setContainerSizes(this.getContainerElement().getBoundingClientRect());

    // Fire an updateAlignment event to any listeners so they are aware of the resolved initial alignment.
    // The initial alignment is determined by the default-aligment attribute, but is subject to being overriden
    // by the persisted state.
    this.dispatchUpdateAlignmentEvent();

    document.addEventListener(`keydown`, this.operatingSystemKeySwitcher);
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (name === `default-alignment`) {
      // Fire an updateAlignment event to any listeners so they are aware of the resolved initial alignment.
      // The initial alignment is determined by the default-aligment attribute, but is subject to being overriden
      // by the persisted state.
      this.dispatchUpdateAlignmentEvent();
    }
    if (name === `fixed-size`) {
      if (!this.attr(`disable-collapsing`) && this.attr(`fixed-size`) && this.isCollapsed()) {
        this.toggleCollapsed({source: ExpandPanelSource.SwitchToFixedSize});
      } else if (!this.attr(`fixed-size`)) {
        this.handleContainerResize();
      }
    }
  }

  isHorizontallyResizable() {
    return HORIZONTAL_DIRECTIONS.has(this.helpers.getResizeDirection());
  }

  isVerticallyResizable() {
    return VERTICAL_DIRECTIONS.has(this.helpers.getResizeDirection());
  }

  isNegativeDirection() {
    return NEGATIVE_DIRECTIONS.has(this.helpers.getResizeDirection());
  }

  /** Changes keys based on operating system we're using and triggers toggleCollapsed */
  operatingSystemKeySwitcher = (ev) => {
    if (!this.attr(`disable-collapsing`) && !this.attr(`fixed-size`) && this.attr(`collapse-key`)) {
      let key = this.attr(`collapse-key`);
      if (this.attr(`collapse-key-modifier`)) {
        key = `${this.attr(`collapse-key-modifier`)}+${key}`;
      }

      return keyEventSwitcher(
        {
          [key]: () => {
            this.toggleCollapsed({source: OtherTogglePanelSource.KeyboardShortcut, keyboardShortcut: key});
          },
        },
        {stopPropagation: true},
      )(ev);
    }
  };

  disconnectedCallback() {
    this.containerResizeObserver?.disconnect();

    document.removeEventListener(`keydown`, this.operatingSystemKeySwitcher);

    super.disconnectedCallback();
  }

  get collapseShortcutLabel() {
    if (!this.attr(`collapse-key`)) {
      return null;
    }

    return generateShortcutLabel(KEYDOWN_EVENTS_CHARACTER[this.attr(`collapse-key`)], {
      ...(this.attr(`collapse-key-modifier`) ? {modifiers: [this.attr(`collapse-key-modifier`)]} : {}),
      forMarkdown: true,
    });
  }

  get defaultSize() {
    return this.getOptimalSize(true);
  }

  /**
   * maxSize is determined either by the `max-size` attr (e.g. sidebar) or if that isn't present, then it is determined by the `min-adjacent-size` attr (e.g. data table).
   */
  get maxSize() {
    if (this.attr(`max-size`)) {
      // this path is currently only used for the sidebar panel
      return this.attr(`max-size`);
    }
    return this.totalSpace - this.attr(`min-adjacent-size`);
  }

  /**
   * Total space available for the current panel and the adjacent element.
   */
  get totalSpace() {
    return this.isVerticallyResizable() ? this.state.containerHeight : this.state.containerWidth;
  }

  startResizing(ev: MouseEvent) {
    if (ev.button !== MOUSE_BUTTONS.LEFT) {
      return;
    }

    // handle a double click event
    // https://developer.mozilla.org/en-US/docs/Web/API/UIEvent/detail
    if (ev.detail === 2) {
      return this.resetResizingState({source: ResetPanelSource.DoubleClick});
    }

    ev.preventDefault();

    this.update({isResizing: true});

    // Capture the initial size of the panel and mouse position for calculating resizes live.
    // this.state.size and mouse position will gradually update as the user drags the panel.
    const initialSize = this.state.size;
    const initialPosition = this.isHorizontallyResizable() ? ev.clientX : ev.clientY;

    this.resizeHandler = (mouseMoveEvent: MouseEvent) => {
      this.onPanelResize(initialPosition, initialSize, mouseMoveEvent);
    };

    window.addEventListener(`mousemove`, this.resizeHandler);
    window.addEventListener(`mouseup`, this.onPanelStopResize);

    // prevent mouse interactions with other elements in the page
    document.body.style.pointerEvents = `none`;
  }

  /**
   * Resets any changes from the user and
   * defaults to the suggested width
   */
  resetResizingState({source}: {source: ResetPanelSource}) {
    this.userLocalStorageHelper.removeItem({
      userId: userCookieUtils.getUserIdentityFromCookie()?.id,
      key: this.attr(`storage-key`),
    });

    this.track(TrackingEvent.Reset, {...this.state, source, totalSpace: this.totalSpace});

    this.update({
      ...DEFAULT_PERSISTED_STATE,
      size: this.defaultSize,
      currentRatio: this.attr(`default-ratio`),
      hasResized: false,
      snapResetState: false,
    });

    this.dispatchResizeEvent();
  }

  private onPanelStopResize = () => {
    document.body.style.pointerEvents = ``;
    window.removeEventListener(`mousemove`, this.resizeHandler);
    window.removeEventListener(`mouseup`, this.onPanelStopResize);
    if (this.state.snapResetState) {
      // when snap to default happens, reset user resizing stored settings
      this.resetResizingState({source: ResetPanelSource.SnapToDefault});
    }
    this.update({isResizing: false});
    this.setPersistedState();
    this.track(TrackingEvent.Resize, {
      size: this.state.size,
      currentRatio: this.state.currentRatio,
      totalSpace: this.totalSpace,
    });
    this.resizeHandler = null;
  };

  private onPanelResize = (initialPosition: number, initialSize: number, ev: MouseEvent) => {
    ev.stopPropagation();
    ev.preventDefault();

    const currentPosition = this.isHorizontallyResizable() ? ev.clientX : ev.clientY;

    const delta = currentPosition - initialPosition;
    const maxSize = this.maxSize;

    let snapResetState = false;
    const newSize = initialSize + (this.isNegativeDirection() ? -delta : delta);
    let newAdjustedSize: number;
    if (maxSize && !this.isCollapsed() && newSize > maxSize) {
      // Cap the size if there's no more room
      newAdjustedSize = maxSize;
    } else if (this.defaultSize && Math.abs(newSize - this.defaultSize) < PANEL_RESIZE_SNAP_WINDOW) {
      // "Snap" to the default width if it's within the window
      newAdjustedSize = this.defaultSize;
      snapResetState = true;
    } else if (this.attr(`min-size`) && maxSize) {
      // Apply min/max bounds
      newAdjustedSize = Math.min(Math.max(newSize, this.attr(`min-size`)), maxSize);
    }

    this.update({size: newAdjustedSize});

    // ratio of the panel's size relative to the total space available.
    const currentRatio = newAdjustedSize / this.totalSpace;
    this.update({currentRatio});

    // If we are hiding collapse button but allowing resizing, don't collapse when resizing to less than min size
    if (
      !this.attr(`show-collapse-button`) &&
      this.attr(`resize-to-collapse-mode`) !== ResizeToCollapseMode.Always &&
      this.attr(`min-size`) &&
      newSize <= this.attr(`min-size`)
    ) {
      this.update({size: initialSize});
      this.onPanelStopResize();
      // collapse when being resized closed to the edge of the screen
    } else if (!this.attr(`disable-collapsing`) && this.attr(`min-size`) && newSize <= this.attr(`min-size`) / 2) {
      this.update({size: initialSize});
      this.onPanelStopResize();
      this.toggleCollapsed({source: CollapsePanelSource.ResizedToEdge});
    } else {
      this.update({hasResized: true, snapResetState});
      this.dispatchResizeEvent();
    }
  };

  private dispatchResizeEvent({
    isCollapsed = this.isCollapsed(),
    togglePanelSource,
  }: {isCollapsed?: boolean; togglePanelSource?: TogglePanelSource} = {}) {
    const size = isCollapsed ? PANEL_COLLAPSED_WIDTH : this.state.size;
    this.dispatchEvent(new CustomEvent(`panelResize`, {detail: {size, isCollapsed, togglePanelSource}}));
  }

  private dispatchUpdateAlignmentEvent() {
    const newAlignment: string = this.helpers.getAlignment();
    this.dispatchEvent(new CustomEvent(`updateAlignment`, {detail: {newAlignment}}));
  }

  // TODO: refactor to avoid mirroring persisted state to component state
  getPersistedState(): PersistedResizablePanelState & {hasResized: boolean} {
    const persistedState = this.userLocalStorageHelper.getItem({
      key: this.attr(`storage-key`),
      userId: userCookieUtils.getUserIdentityFromCookie()?.id,
    });

    // Use helper func to get isCollapsed state so that it stays in sync with other callsites that access this state.
    const isCollapsed = isResizablePanelCollapsed({
      localStorageManager: this.getContext(`localStorageManager`),
      storageKey: this.attr(`storage-key`),
    });

    if (!persistedState) {
      return {
        ...DEFAULT_PERSISTED_STATE,
        size: this.defaultSize,
        currentRatio: this.attr(`default-ratio`),
        hasResized: false,
        ...(this.isVisibilityPersisted() && {isCollapsed}),
      };
    }

    const size = persistedState.size
      ? Math.max(this.attr(`min-size`), Math.min(persistedState.size, this.maxSize))
      : this.defaultSize;

    const currentRatio = size / this.totalSpace;

    return {
      ...(this.isVisibilityPersisted() && {isCollapsed}),
      alignment: persistedState.alignment,
      size,
      hasResized: Boolean(persistedState.size),
      currentRatio,
    };
  }

  private setPersistedState() {
    const persistedState = {
      ...(this.isVisibilityPersisted() && {isCollapsed: this.isCollapsed()}),
      alignment: this.state.alignment,
      size: this.state.hasResized ? this.state.size : null,
      currentRatio: this.state.currentRatio,
    };

    this.userLocalStorageHelper.setItem({
      userId: userCookieUtils.getUserIdentityFromCookie()?.id,
      key: this.attr(`storage-key`),
      value: persistedState,
    });
  }

  isVisibilityPersisted() {
    return this.attr(`visibility`) === PanelVisibility.Auto;
  }

  isCollapsed() {
    return this.isVisibilityPersisted()
      ? this.state.isCollapsed
      : this.attr(`visibility`) === PanelVisibility.Collapsed;
  }

  handleContainerResize() {
    if (!this.attr(`fixed-size`)) {
      if (this.state.containerWidth && this.state.containerHeight && !this.hasDispatchedResize) {
        this.update(this.getPersistedState());
        this.dispatchResizeEvent();
        this.hasDispatchedResize = true;
      } else {
        const newSize = this.getOptimalSize();
        // prevent automatic resizing when the sidebar is in overlay mode
        if (this.state.size !== newSize) {
          this.update({size: newSize});
          this.update({currentRatio: newSize / this.totalSpace});
          this.dispatchResizeEvent();
          this.applyStyles();
        }

        this.applyStyles();
      }
    } else {
      this.update({size: this.attr(`default-size`)});
      this.dispatchResizeEvent();
    }
  }

  toggleCollapsed({source, keyboardShortcut}: {source: TogglePanelSource; keyboardShortcut?: string}) {
    const isCollapsed = !this.isCollapsed();

    if (this.isVisibilityPersisted()) {
      this.update({isCollapsed});
      this.setPersistedState();
    }
    this.dispatchResizeEvent({isCollapsed, togglePanelSource: source});
    this.track(isCollapsed ? TrackingEvent.CollapsedPanel : TrackingEvent.ExpandedPanel, {
      source,
      size: this.state.size,
      currentRatio: this.state.currentRatio,
      totalSpace: this.totalSpace,
      keyboardShortcut,
    });
  }

  toggleAlignment() {
    const prevAlignment = this.helpers.getAlignment();
    const alignment = OPPOSITE_DIRECTION[prevAlignment];
    this.update({alignment});
    this.dispatchResizeEvent();
    this.setPersistedState();
    this.dispatchUpdateAlignmentEvent();
    this.track(TrackingEvent.ToggleAlignment, {
      source: ToggleAlignmentSource.Button,
      prevAlignment,
      alignment,
      size: this.state.size,
      currentRatio: this.state.currentRatio,
      totalSpace: this.totalSpace,
    });
  }

  private track(eventName: TrackingEvent, props = {}) {
    emitTrackEvent(this, {
      eventName,
      eventPrefix: COMPONENT_NAME,
      props: {
        tab: this.getContext(`reportInfo`).getReportName(),
        panelName: this.attr(`panel-name`),
        ...props,
      },
    });
  }

  set headerContent(el) {
    this._headerContent = el;
    this.update();
  }

  set footerContent(el) {
    this._footerContent = el;
    this.update();
  }

  set containerEl(val) {
    this._containerElement = val;
  }

  /**
   * Defines the panel size as this.attr(`default-ratio`)% of the container size.
   *
   * Sidebar is clamped by the MIN width and 560px as MAX for the
   * default provided widths
   */
  private getOptimalSize(defaultSize?: boolean) {
    if (!this.totalSpace) {
      return this.attr(`default-size`);
    }

    const relativeSize = Math.floor(
      (defaultSize ? this.attr(`default-ratio`) : this.state.currentRatio) * this.totalSpace,
    );
    return Math.max(
      this.attr(`min-size`),
      Math.min(relativeSize, !defaultSize || this.isVerticallyResizable() ? this.maxSize : PANEL_MAX_AUTO_WIDTH),
    );
  }

  private applyStyles() {
    const styleAttr = this.isHorizontallyResizable() ? `width` : `height`;
    this.style[styleAttr] = this.isCollapsed() ? `` : `${this.state.size}px`;

    this.isCollapsed() ? this.classList.add(`collapsed`) : this.classList.remove(`collapsed`);
  }

  showAlignmentToggle(): boolean {
    return this.attr(`show-alignment-toggle`) || this.helpers.getAlignment() === Direction.Right;
  }
}

export const ResizablePanelEl = defineAndMakeJsxFactory<ResizablePanelJsxProps>(COMPONENT_NAME, ResizablePanel);
export const ResizablePanelReact = panel2react<ResizablePanelJsxProps>(COMPONENT_NAME, ResizablePanel);
